import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AuthSession {
  token: string;
  userId: number;
  name: string;
  email: string;
  role: "admin" | "editor" | "viewer";
  clientId: number | null;
  isRazAdmin: boolean;
  /** Client currently active (para raz admin que pode trocar) */
  activeClientId: number | null;
  /** Setada no 1º login com senha temporária — força redirect pra /redefinir-senha */
  mustChangePassword?: boolean;
}

interface AuthCtx {
  session: AuthSession | null;
  isLoading: boolean;
  login: (result: Omit<AuthSession, "activeClientId">) => void;
  logout: () => void;
  setActiveClient: (clientId: number | null) => void;
  /** Limpa o flag mustChangePassword depois que a senha foi trocada */
  clearMustChangePassword: () => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthCtx | null>(null);

export const CLIENT_AUTH_KEY = "socialhub_client_session";

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState<AuthSession | null>(null);

  // Hydrate from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(CLIENT_AUTH_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as AuthSession;
        // Basic sanity check
        if (parsed.token && parsed.userId) {
          setSession(parsed);
        }
      }
    } catch {
      localStorage.removeItem(CLIENT_AUTH_KEY);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback((result: Omit<AuthSession, "activeClientId">) => {
    const sess: AuthSession = {
      ...result,
      activeClientId: result.clientId,
    };
    localStorage.setItem(CLIENT_AUTH_KEY, JSON.stringify(sess));
    setSession(sess);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(CLIENT_AUTH_KEY);
    setSession(null);
    navigate("/login");
  }, [navigate]);

  const setActiveClient = useCallback((clientId: number | null) => {
    setSession(prev => {
      if (!prev) return prev;
      const updated = { ...prev, activeClientId: clientId };
      localStorage.setItem(CLIENT_AUTH_KEY, JSON.stringify(updated));
      return updated;
    });
    // Invalida TODOS os caches do React Query — força refetch das queries
    // tenant-scoped (Pautas, Temas, Métricas, Insights, etc) com o novo
    // header x-active-client que main.tsx vai mandar a partir da próxima request.
    queryClient.invalidateQueries();
  }, [queryClient]);

  const clearMustChangePassword = useCallback(() => {
    setSession(prev => {
      if (!prev) return prev;
      const updated = { ...prev, mustChangePassword: false };
      localStorage.setItem(CLIENT_AUTH_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ session, isLoading, login, logout, setActiveClient, clearMustChangePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
